import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Tag } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { img, inrFmt } from "@/lib/format";
import useSEO from "@/hooks/useSEO";

export default function Cart() {
  const {
    cart, updateQty, removeFromCart, subtotalInr, subtotalUsd, discountInr, discountUsd,
    promo, applyPromo, fmt, config,
  } = useStore();
  const [code, setCode] = useState("");
  useSEO({ title: "Your Bag — Just Kords", description: "Review your Just Kords co-ord sets before checkout." });

  const threshold = config.free_shipping_threshold || 2999;
  const shipping = subtotalInr >= threshold ? 0 : config.shipping_fee || 99;
  const totalInr = subtotalInr - discountInr + shipping;
  const totalUsd = subtotalUsd - discountUsd + (subtotalInr >= threshold ? 0 : (config.shipping_fee || 99) / (config.usd_rate || 83));

  if (cart.length === 0) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 bg-alabaster pt-28 text-center" data-testid="cart-empty">
        <ShoppingBag size={44} strokeWidth={1.2} className="text-linec" />
        <p className="font-serif text-4xl">Your bag is empty.</p>
        <p className="text-sm text-mutedc">Every great outfit starts with a set.</p>
        <Link to="/shop" data-testid="cart-empty-shop-cta" className="mt-2 rounded-full bg-crimson px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark">
          Shop the Collection
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-alabaster pt-28 lg:pt-36">
      <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <h1 className="border-b border-linec pb-8 font-serif text-5xl font-light" data-testid="cart-title">Your Bag</h1>
        <div className="grid gap-12 py-10 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2" data-testid="cart-items">
            {cart.map((item) => (
              <div key={`${item.slug}:${item.size}`} data-testid={`cart-item-${item.slug}`} className="flex gap-5 border-b border-linec pb-6">
                <Link to={`/product/${item.slug}`} className="shrink-0">
                  <img src={img(item.image, 300)} alt={item.name} className="h-36 w-28 object-cover" />
                </Link>
                <div className="flex flex-1 flex-col justify-between">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Link to={`/product/${item.slug}`} className="font-serif text-xl transition-colors hover:text-crimson">{item.name}</Link>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-mutedc">Size {item.size}</p>
                    </div>
                    <button data-testid={`cart-remove-${item.slug}`} onClick={() => removeFromCart(item.slug, item.size)} aria-label="Remove" className="p-1 text-mutedc transition-colors hover:text-crimson">
                      <Trash2 size={17} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border border-linec bg-white">
                      <button data-testid={`cart-qty-minus-${item.slug}`} onClick={() => updateQty(item.slug, item.size, item.qty - 1)} className="px-3 py-2 transition-colors hover:text-crimson" aria-label="Decrease"><Minus size={14} /></button>
                      <span data-testid={`cart-qty-${item.slug}`} className="w-8 text-center font-mono text-sm">{item.qty}</span>
                      <button data-testid={`cart-qty-plus-${item.slug}`} onClick={() => updateQty(item.slug, item.size, item.qty + 1)} className="px-3 py-2 transition-colors hover:text-crimson" aria-label="Increase"><Plus size={14} /></button>
                    </div>
                    <p className="font-semibold">{fmt(item.price_inr * item.qty, item.price_usd * item.qty)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <div className="sticky top-36 space-y-5 border border-linec bg-white p-7" data-testid="cart-summary">
              <h2 className="font-serif text-2xl">Summary</h2>

              <div className="flex items-center gap-2 border border-linec p-1.5">
                <Tag size={15} className="ml-2 text-crimson" />
                <input
                  data-testid="cart-promo-input" value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="Promo code (try JUST10)"
                  className="w-full bg-transparent text-sm placeholder:text-mutedc focus:outline-none"
                />
                <button
                  data-testid="cart-promo-apply"
                  onClick={() => {
                    const ok = applyPromo(code);
                    toast[ok ? "success" : "error"](ok ? "JUST10 applied — 10% off" : "That code isn't valid");
                  }}
                  className="rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-crimson"
                >
                  Apply
                </button>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-mutedc">Subtotal</span><span data-testid="cart-subtotal">{fmt(subtotalInr, subtotalUsd)}</span></div>
                {promo && (
                  <div className="flex justify-between text-crimson" data-testid="cart-discount"><span>JUST10 (−10%)</span><span>−{fmt(discountInr, discountUsd)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-mutedc">Shipping</span><span data-testid="cart-shipping">{shipping === 0 ? "Free" : fmt(shipping, (config.shipping_fee || 99) / (config.usd_rate || 83))}</span></div>
                <div className="flex justify-between border-t border-linec pt-3">
                  <span className="font-semibold">Total</span>
                  <span data-testid="cart-total" className="font-serif text-2xl">{fmt(totalInr, totalUsd)}</span>
                </div>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">
                {subtotalInr >= threshold ? "Free shipping unlocked" : `${inrFmt(threshold - subtotalInr)} away from free shipping`}
              </p>
              <Link to="/checkout" data-testid="cart-checkout-cta" className="block rounded-full bg-crimson py-4 text-center text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson-dark">
                Proceed to Checkout
              </Link>
              <p className="text-center font-mono text-[10px] uppercase tracking-widest text-mutedc">UPI · Cards · NetBanking · COD</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
